import { useApolloClient } from '@apollo/client'

import {
  useAnnotationResolvedSubscription,
  useAnnotationUnresolvedSubscription,
} from '@sketch/gql-types'

import { removeFromPaginated } from '../../utils/apolloCache'
import { updateCommentsQuery } from '../utils'

import useCommentSubscriptions from './useCommentSubscriptions'

interface UseCommentThreadSubscriptionsParams {
  shareIdentifier: string
  skip?: boolean
}

/**
 * __useCommentThreadSubscriptions__
 *
 * Keeps a single comment thread live for surfaces that show it outside the
 * document — chiefly the inbox preview, which isn't covered by the
 * document-level `AnnotationSubscriptions` container. Don't mount both for the
 * same share, or they'll double-subscribe.
 *
 * Subscribes to every annotation/comment event that affects the thread:
 *
 * - __resolved / unresolved__ — both return a `BaseAnnotation`, so Apollo
 *   merges `resolution` straight into the normalized `Annotation` entity and
 *   `AnnotationCommentsList` swaps the reply box for the resolution footer
 *   (this is the WEB-3726 fix). No manual cache write needed.
 * - __reply created__ — inserted into the `getAnnotationComments` list.
 * - __reply deleted__ — removed from the list.
 * - __reply edited / marked as read__ — no manual write: the subscription
 *   payloads update the normalized `Comment` entity in place, so the list
 *   re-renders on its own.
 *
 * The comment events are share-scoped, so they fire for every thread in the
 * share; the cache writes only touch the thread whose `getAnnotationComments`
 * query is actually cached (`updateCommentsQuery` no-ops otherwise), which in
 * the inbox is the one open preview.
 */
const useCommentThreadSubscriptions = ({
  shareIdentifier,
  skip,
}: UseCommentThreadSubscriptionsParams) => {
  const apollo = useApolloClient()

  const variables = { shareIdentifier }
  useAnnotationResolvedSubscription({ variables, skip })
  useAnnotationUnresolvedSubscription({ variables, skip })

  useCommentSubscriptions(
    shareIdentifier,
    undefined,
    (type, data) => {
      if (type === 'created') {
        const comment = data.annotationReplyCreated

        updateCommentsQuery(
          apollo,
          { annotationIdentifier: comment.annotation.identifier },
          draft => {
            const { comments } = draft.annotation!

            // Apollo may already have merged the comment by identifier — don't
            // add it to the list a second time.
            const alreadyListed = comments.entries.some(
              ({ identifier }) => identifier === comment.identifier
            )
            if (alreadyListed) return

            comments.entries.unshift(comment)
            comments.meta.totalCount++
          }
        )
      } else if (type === 'deleted') {
        const { identifier } = data.annotationReplyDeleted

        removeFromPaginated(
          apollo,
          { __typename: 'Comment', identifier },
          () => true
        )
      }
    },
    skip
  )
}

export default useCommentThreadSubscriptions
